// src/pages/ocfEpisodeBodies.jsx
// Corpo (contenuto) degli articoli-episodio OCF, mappato per slug.
// Fonte: master doc in _ocf_handoff (verificati 2026-06). Contenuto educativo di
// ripasso per la prova valutativa OCF — nessuna raccomandazione (disclaimer nel
// componente OcfEpisode). I body sono in italiano (OCF è un ente italiano).
import React from "react";

export const OCF_EPISODE_BODIES = {
  // ── EP-17 · Sanzioni e onorabilità (master doc OCF-17) ────────────────────
  "sanzioni-onorabilita": (
    <>
      <p>
        Il sistema sanzionatorio dei consulenti finanziari è uno dei capitoli più temuti della prova
        OCF: si gioca tutto su distinzioni precise. Qui le mettiamo in ordine.
      </p>

      <h2>Le tre fonti</h2>
      <ul>
        <li><strong>TUF</strong> (d.lgs. 58/1998) — misure più gravi e sospensione cautelare.</li>
        <li><strong>Regolamento Intermediari CONSOB</strong> (Delibera 20307/2018) — gradua le sanzioni (art. 180) e i requisiti di accesso.</li>
        <li><strong>DM Tesoro 472/1998</strong> — requisiti di onorabilità.</li>
      </ul>
      <p>L'organo che tiene l'albo unico e applica gran parte delle sanzioni è l'<strong>OCF</strong>.</p>

      <h2>La scala delle sanzioni (dalla più lieve alla più grave)</h2>
      <ul>
        <li><strong>Richiamo scritto</strong> — inadempienze minori.</li>
        <li><strong>Sanzione pecuniaria</strong> — violazioni amministrative (mancata comunicazione del cambio di domicilio, mancata consegna delle informative, mancata identificazione del cliente prima del contratto).</li>
        <li><strong>Sospensione 1-4 mesi</strong> — violazioni comportamentali gravi (mancato aggiornamento professionale, carenze informative, attività incompatibili, conflitti di interesse, mancata collaborazione con l'OCF).</li>
        <li><strong>Radiazione</strong> — condotte che colpiscono la fiducia: contraffazione della firma, ricezione o detenzione (anche temporanea) di somme del cliente, documenti non veri all'OCF, inosservanza di una sospensione, violazione dei requisiti di indipendenza.</li>
      </ul>

      <h2>Sospensione cautelare: 180 giorni o un anno</h2>
      <p>
        Da non confondere con la sospensione <em>sanzionatoria</em> (1-4 mesi). La{" "}
        <strong>cautelare</strong> (art. 7-septies TUF) non punisce un illecito già accertato ma
        blocca in via preventiva l'attività: <strong>fino a 180 giorni</strong> per necessità e
        urgenza; <strong>fino a un anno</strong> quando il consulente è <strong>imputato</strong>
        {" "}(delitti contro il patrimonio, contro l'economia pubblica, in materia tributaria, falso
        in prospetto, abusiva raccolta del risparmio). Punto chiave: essere imputati porta alla
        sospensione cautelare, <strong>non</strong> alla radiazione.
      </p>

      <h2>Radiazione e cancellazione</h2>
      <p>
        La radiazione comporta sempre l'istantanea cancellazione, ma non vale il contrario: la
        cancellazione può essere <strong>amministrativa</strong> (mancato contributo OCF, perdita
        dell'onorabilità, inattività della società). Dalle cancellazioni amministrative ci si
        reiscrive a domanda una volta rimossa la causa; dopo una <strong>radiazione</strong> la
        reiscrizione è possibile solo dopo <strong>cinque anni</strong> dalla notifica.
      </p>

      <h2>Onorabilità e divieti di iscrizione</h2>
      <p>
        Per i <strong>reati "di settore"</strong> (mercati e valori mobiliari, attività finanziaria,
        bancaria, assicurativa) la condanna definitiva alla pena detentiva preclude l'iscrizione{" "}
        <strong>a prescindere dalla durata</strong> (salvi gli effetti della riabilitazione); per gli{" "}
        <strong>altri reati gravi</strong> il divieto scatta con reclusione <strong>non inferiore a
        un anno</strong>. Per partecipare alla prova valutativa serve almeno il <strong>diploma di
        istruzione secondaria superiore</strong>.
      </p>

      <h2>Le mnemoniche</h2>
      <p>
        <strong>Le 5 P</strong> (dalla meno alla più grave): <strong>P</strong>ecuniaria →{" "}
        <strong>P</strong>untura (richiamo scritto) → <strong>P</strong>ausa (sospensione) →{" "}
        <strong>P</strong>ortiera (radiazione, sei fuori) → <strong>P</strong>roibizione (divieto di
        iscrizione).
      </p>
      <p>
        <strong>Il criterio del "tocco":</strong> tocchi il denaro o la firma del cliente, o ignori
        uno stop già imposto → <strong>radiazione</strong>; non rispetti procedure/informazioni
        oppure sei imputato → <strong>sospensione</strong>.
      </p>

      <h2>Domande d'esame ricorrenti</h2>
      <p><strong>Imputato per un delitto contro il patrimonio?</strong> Sospensione cautelare (max un anno), non radiazione.</p>
      <p><strong>Detiene, anche solo temporaneamente, somme del cliente?</strong> Radiazione dall'albo.</p>
      <p><strong>Non comunica il cambio di domicilio all'OCF?</strong> Sanzione pecuniaria (eventuale richiamo scritto).</p>
      <p><strong>Solo licenza di terza media: può sostenere la prova?</strong> No: serve la scuola secondaria superiore.</p>
      <p><strong>Una SIM paga la sanzione del proprio consulente: deve agire in regresso?</strong> Sì, in ogni caso (art. 196 TUF).</p>
    </>
  ),

  // ── EP-19 · Le obbligazioni (master doc OCF-18, Parte 1) ──────────────────
  "obbligazioni": (
    <>
      <h2>Cos'è un'obbligazione e quali sono le fonti</h2>
      <p>
        L'obbligazione è il rapporto giuridico per cui un <strong>debitore</strong> è tenuto a una
        prestazione economicamente valutabile verso un <strong>creditore</strong> (art. 1174 c.c.);
        l'interesse del creditore può anche non essere patrimoniale. Le fonti sono il contratto, il
        fatto illecito e ogni altro atto o fatto idoneo a produrle (art. 1173).
      </p>

      <h2>Come si classificano</h2>
      <p>
        Con più debitori la regola è la <strong>solidarietà passiva</strong>, presunta dalla legge
        (art. 1294): il creditore può chiedere l'intero a uno qualsiasi dei condebitori, salvo il
        regresso interno. Per le obbligazioni <strong>pecuniarie</strong> vale il <strong>principio
        nominalistico</strong> (art. 1277): si paga al valore nominale in moneta avente corso legale.
        Gli interessi convenzionali sopra la misura legale richiedono forma scritta (art. 1284). Le{" "}
        <strong>obbligazioni naturali</strong> (art. 2034) non sono coercibili, ma ciò che si è pagato
        spontaneamente non si può ripetere.
      </p>

      <h2>Adempimento, mora e inadempimento</h2>
      <p>
        L'adempimento va eseguito con la <strong>diligenza del buon padre di famiglia</strong> (art.
        1176); può adempiere anche un terzo (art. 1180). In caso di inadempimento il debitore deve il
        risarcimento, salvo provi che l'impossibilità deriva da causa a lui <strong>non
        imputabile</strong> (art. 1218). La <strong>mora</strong> richiede di norma intimazione
        scritta (art. 1219), ma è <strong>automatica</strong> per il debito da fatto illecito, per la
        dichiarazione scritta di non voler adempiere e alla scadenza del termine se la prestazione va
        eseguita al domicilio del creditore. Il danno comprende danno emergente e lucro cessante
        (art. 1223), limitato a ciò che era prevedibile salvo dolo (art. 1225).
      </p>

      <h2>Come si estingue</h2>
      <p>
        Oltre all'adempimento: <strong>compensazione</strong> (1241), <strong>confusione</strong>
        {" "}(1253), <strong>novazione</strong> (1230), <strong>remissione</strong> (1236) e{" "}
        <strong>impossibilità sopravvenuta</strong> per causa non imputabile (1256). La{" "}
        <strong>prescrizione ordinaria è di dieci anni</strong> (art. 2946). La clausola penale (art.
        1382) predetermina il risarcimento ma è <strong>riducibile dal giudice</strong> se
        manifestamente eccessiva (art. 1384).
      </p>

      <h2>Mnemoniche e trabocchetti</h2>
      <ul>
        <li>Solidarietà passiva = <strong>presunta</strong> (non serve un patto).</li>
        <li>Obbligazione naturale = <strong>non coercibile ma non ripetibile</strong>.</li>
        <li>Mora del debitore: a volte è <strong>automatica</strong> (fatto illecito, rifiuto scritto, termine al domicilio del creditore).</li>
      </ul>

      <h2>Domande d'esame</h2>
      <p><strong>Più condebitori: a chi può chiedere l'intero il creditore?</strong> A uno qualsiasi (solidarietà passiva presunta, art. 1294).</p>
      <p><strong>Ciò che si paga per un'obbligazione naturale si può chiedere indietro?</strong> No (art. 2034).</p>
      <p><strong>Il danno imprevedibile è sempre risarcibile?</strong> Solo in caso di dolo (art. 1225).</p>
    </>
  ),

  // ── EP-20 · La responsabilità civile (master doc OCF-18, Parte 2) ─────────
  "responsabilita-civile": (
    <>
      <h2>Contrattuale vs extracontrattuale: la distinzione chiave</h2>
      <p>
        La responsabilità <strong>contrattuale</strong> (art. 1218) nasce dall'inadempimento di
        un'obbligazione preesistente: si prescrive in <strong>dieci anni</strong> e l'onere della
        prova è ripartito — il creditore prova il titolo e allega l'inadempimento, il debitore deve
        provare l'impossibilità a sé non imputabile. La <strong>extracontrattuale</strong> (o
        aquiliana, art. 2043) nasce dalla violazione del generale dovere di "neminem laedere": si
        prescrive in <strong>cinque anni</strong> (art. 2947), ridotti a <strong>due</strong> per i
        danni da circolazione di veicoli, e qui è il <strong>danneggiato</strong> a dover provare
        tutto (fatto, danno ingiusto, dolo o colpa, nesso di causalità).
      </p>

      <h2>Le responsabilità speciali (artt. 2050-2054)</h2>
      <ul>
        <li><strong>Attività pericolose</strong> (2050): ci si libera solo provando di aver adottato <strong>tutte le misure idonee</strong> a evitare il danno.</li>
        <li><strong>Cose in custodia</strong> (2051) e <strong>animali</strong> (2052): liberazione solo provando il <strong>caso fortuito</strong>.</li>
        <li><strong>Circolazione di veicoli</strong> (2054): presunzione a carico del conducente, che deve provare di aver fatto tutto il possibile.</li>
        <li><strong>Padroni e committenti</strong> (2049): responsabilità <strong>oggettiva</strong> per i fatti dei dipendenti.</li>
      </ul>
      <p>
        Non c'è responsabilità se manca l'imputabilità (2046) o se ricorre una causa di
        giustificazione come la legittima difesa (2044) o lo stato di necessità (2045). Il{" "}
        <strong>danno non patrimoniale</strong> (2059) è risarcibile nei casi previsti dalla legge.
      </p>

      <h2>Mnemoniche</h2>
      <ul>
        <li><strong>Prescrizioni "10-5-2":</strong> contrattuale 10, extracontrattuale 5, RC auto 2.</li>
        <li><strong>Onere della prova:</strong> contrattuale → il debitore prova l'impossibilità; extracontrattuale → il danneggiato prova tutto.</li>
        <li><strong>Liberatorie:</strong> custodia e animali → solo caso fortuito; attività pericolose → tutte le misure idonee.</li>
      </ul>

      <h2>Domande d'esame</h2>
      <p><strong>Differenza di prescrizione tra contrattuale ed extracontrattuale?</strong> 10 anni contro 5 (2 per RC auto).</p>
      <p><strong>Chi prova cosa nell'extracontrattuale?</strong> Il danneggiato prova fatto, danno, colpa/dolo e nesso causale.</p>
      <p><strong>Danno da cosa in custodia: come ci si libera?</strong> Solo provando il caso fortuito (art. 2051).</p>
    </>
  ),

  // ── EP-21 · Altri tributi 2026 (master doc OCF-19) ────────────────────────
  "altri-tributi-2026": (
    <>
      <p>
        Oltre alle imposte sui redditi degli investimenti, esiste una costellazione di tributi
        "minori" che pesano sul patrimonio finanziario — e che l'esame OCF chiede proprio perché
        vengono trascurati.
      </p>

      <h2>Tobin tax: cosa cambia nel 2026</h2>
      <p>
        L'imposta sulle transazioni finanziarie colpisce il trasferimento della proprietà di azioni
        di società <strong>italiane</strong> con capitalizzazione media pari o superiore a{" "}
        <strong>500 milioni di euro</strong> (sotto questa soglia, escluse). Dal <strong>1° gennaio
        2026</strong> (L. 199/2025) le aliquote <strong>raddoppiano</strong>: mercati regolamentati e
        MTF dallo 0,10% allo <strong>0,20%</strong>, operazioni OTC dallo 0,20% allo{" "}
        <strong>0,40%</strong>, alta frequenza dallo 0,02% allo <strong>0,04%</strong>. Trappola
        d'esame: i <strong>derivati non sono stati raddoppiati</strong> (restano a importi fissi).
      </p>

      <h2>Imposta di bollo e di registro</h2>
      <p>
        Il <strong>bollo</strong> sui prodotti finanziari è lo <strong>0,20% annuo</strong> sul
        controvalore del deposito titoli; il tetto di <strong>14.000€</strong> vale <strong>solo per
        i soggetti diversi dalle persone fisiche</strong> (per le persone fisiche non c'è alcun
        tetto). Il <strong>registro</strong> è marginale: la scrittura privata non autenticata sconta
        <strong> 200€ in misura fissa</strong>, solo "in caso d'uso".
      </p>

      <h2>Successioni e donazioni (aliquote e franchigie)</h2>
      <ul>
        <li>Coniuge e parenti in linea retta: <strong>4%</strong>, franchigia <strong>1.000.000€ per beneficiario</strong>.</li>
        <li>Fratelli e sorelle: <strong>6%</strong>, franchigia <strong>100.000€</strong>.</li>
        <li>Altri parenti fino al 4° grado e affini: <strong>6% senza franchigia</strong>.</li>
        <li>Tutti gli altri soggetti: <strong>8% senza franchigia</strong>. Disabilità grave: franchigia <strong>1.500.000€</strong>.</li>
      </ul>
      <p>Dal 2025 vige l'<strong>autoliquidazione</strong> e l'abolizione del "coacervo" (D.Lgs. 139/2024).</p>

      <h2>Trust, IVA e attività estere</h2>
      <p>
        Il <strong>trust</strong> è tassato in base ai beneficiari (se non individuati, è soggetto
        passivo IRES). Le operazioni finanziarie sono <strong>esenti IVA</strong>, <strong>ma la
        gestione individuale di portafogli è imponibile</strong> (classico tranello d'esame). Per le
        attività estere: <strong>IVAFE</strong> 2 per mille (4 nei paradisi fiscali) e{" "}
        <strong>IVIE</strong> 1,06% — approfondite nell'episodio dedicato alle attività estere.
      </p>

      <h2>Mnemoniche</h2>
      <ul>
        <li><strong>Tobin "il doppio dal 2026":</strong> regolamentato 0,20%, OTC 0,40% — ma i derivati no.</li>
        <li><strong>Successioni "4-6-8":</strong> 4% coniuge/figli (1 mln), 6% fratelli (100k), 8% estranei (zero).</li>
        <li><strong>IVA:</strong> finanza esente, gestione di portafogli no.</li>
      </ul>

      <h2>Domande d'esame</h2>
      <p><strong>Aliquota Tobin tax 2026 su azioni quotate?</strong> 0,20% (raddoppiata dallo 0,10%).</p>
      <p><strong>C'è un tetto al bollo per una persona fisica?</strong> No (il tetto di 14.000€ vale solo per i non-persone-fisiche).</p>
      <p><strong>La gestione individuale di portafogli è esente IVA?</strong> No, è imponibile.</p>
    </>
  ),

  // ── EP-23 · Attività estere: IVAFE, IVIE, quadro RW (master doc OCF-23) ────
  "attivita-estere-ivafe-ivie": (
    <>
      <p>
        Chi è residente fiscale in Italia paga le imposte sui propri redditi e patrimoni ovunque
        siano nel mondo (<em>worldwide taxation</em>). Le attività detenute all'estero comportano due
        obblighi: uno <strong>dichiarativo</strong> (il quadro RW) e uno <strong>patrimoniale</strong>
        {" "}(le imposte IVAFE e IVIE).
      </p>

      <h2>Il quadro RW (monitoraggio fiscale)</h2>
      <p>
        Nel <strong>quadro RW</strong> si indicano le attività estere (conti, titoli, immobili,
        partecipazioni, cripto-attività) <strong>anche se non producono reddito</strong>, per
        finalità di trasparenza e antiriciclaggio; lì si liquidano anche IVAFE e IVIE. C'è un esonero
        tipico quando le attività finanziarie sono affidate a un <strong>intermediario
        residente</strong> che applica ritenute e bollo. L'omessa o infedele compilazione è
        sanzionata in misura proporzionale, più severamente per gli Stati a fiscalità privilegiata.
      </p>

      <h2>IVAFE</h2>
      <p>
        Imposta sul valore delle attività finanziarie detenute all'estero: aliquota ordinaria{" "}
        <strong>2 per mille</strong> (0,20%) sul valore di mercato, <strong>4 per mille</strong> se le
        attività sono in Stati a fiscalità privilegiata. Per i conti correnti e i libretti esteri si
        applica invece un importo fisso di <strong>34,20€</strong> per ciascun conto, dovuto solo se
        la giacenza media annua supera i <strong>5.000€</strong>.
      </p>

      <h2>IVIE</h2>
      <p>
        Imposta sul valore degli immobili situati all'estero: aliquota <strong>1,06%</strong>
        {" "}(innalzata dal precedente 0,76%), calcolata sul costo d'acquisto o sul valore catastale
        estero a seconda del Paese. Sono previsti meccanismi per evitare la doppia imposizione con le
        imposte patrimoniali pagate nello Stato estero.
      </p>

      <h2>Mnemoniche</h2>
      <ul>
        <li><strong>"2-4-34":</strong> IVAFE 2‰ normale, 4‰ paradisi, 34,20€ sui conti correnti.</li>
        <li><strong>RW = "dichiari"; IVAFE/IVIE = "paghi".</strong></li>
      </ul>

      <h2>Domande d'esame</h2>
      <p><strong>Aliquota IVAFE ordinaria sui titoli esteri?</strong> 2 per mille.</p>
      <p><strong>Conto estero con giacenza media 4.000€: quanto IVAFE?</strong> Zero (sotto i 5.000€).</p>
      <p><strong>A cosa serve il quadro RW?</strong> Al monitoraggio fiscale delle attività estere (e alla liquidazione di IVAFE/IVIE).</p>
    </>
  ),

  // ── EP-9 · PIR e gestione patrimoniale (trascrizione video) ───────────────
  "pir-gestione-patrimoniale": (
    <>
      <p>
        Questo episodio mette a fuoco due strumenti che l'esame OCF ama: i <strong>PIR</strong>
        {" "}(Piani Individuali di Risparmio) e le <strong>gestioni individuali di portafoglio</strong>.
        Il filo comune è il <em>regime fiscale</em>.
      </p>

      <h2>Cosa sono i PIR</h2>
      <p>
        Piani di risparmio di <strong>lungo termine</strong> nati per incentivare l'investimento
        nell'economia reale italiana ed europea. Possono contenere partecipazioni non qualificate,
        liquidità (depositi e conti correnti) e titoli di Stato, italiani ed esteri. Il punto di forza
        è il <strong>regime agevolato</strong>: possibile <strong>esenzione</strong> sui redditi
        finanziari generati — ma <strong>non automatica</strong>: dipende da limiti, composizione e
        durata.
      </p>

      <h2>PIR tradizionali: limiti e composizione</h2>
      <ul>
        <li>Versamento massimo <strong>40.000€ l'anno</strong>, fino a un tetto complessivo di <strong>200.000€</strong>.</li>
        <li>Composizione (per almeno <strong>2/3 dell'anno</strong>): almeno <strong>3,5%</strong> in strumenti di società IT/UE/SEE con stabile organizzazione in Italia e <em>non</em> incluse negli indici FTSE MIB / FTSE Mid Cap; e almeno <strong>17,5%</strong> in strumenti di imprese diverse da quelle del FTSE MIB. La logica: indirizzare il risparmio verso le imprese a minore capitalizzazione (PMI).</li>
        <li>Durata: gli strumenti vanno detenuti per un minimo di <strong>5 anni</strong> — è la condizione chiave per l'esenzione.</li>
      </ul>

      <h2>PIR alternativi</h2>
      <p>
        Un'altra categoria, pensata per l'economia reale <strong>non quotata</strong> (private equity,
        venture capital). Limiti molto più alti: <strong>300.000€ l'anno</strong>, fino a{" "}
        <strong>1.500.000€</strong> complessivi. Oltre all'esenzione sui redditi finanziari, possono
        dare l'<strong>esenzione dall'imposta di successione</strong> e il riconoscimento di un{" "}
        <strong>credito d'imposta</strong>.
      </p>

      <h2>Cosa succede se salti i requisiti</h2>
      <p>
        Il mancato rispetto di limiti, composizione o dei 5 anni comporta la <strong>decadenza</strong>
        {" "}dal regime agevolato: si pagano le imposte originariamente non versate sui rendimenti, più
        gli interessi. Importante: <strong>non si applicano sanzioni punitive</strong>, solo l'imposta
        dovuta.
      </p>

      <h2>Le gestioni individuali di portafoglio</h2>
      <p>
        Nel <strong>risparmio gestito</strong> il cliente affida un mandato a un intermediario, che
        applica un'<strong>imposta sostitutiva sul risultato di gestione maturato</strong> nell'anno.
        Il vantaggio operativo è la <strong>compensazione interna</strong> tra plusvalenze e
        minusvalenze all'interno del mandato. Da ricordare (tranello d'esame): la gestione individuale
        di portafogli è <strong>imponibile IVA</strong>, a differenza della generalità delle
        operazioni finanziarie che sono esenti.
      </p>

      <h2>Domande d'esame</h2>
      <p><strong>Versamento massimo annuo in un PIR tradizionale?</strong> 40.000€ (tetto totale 200.000€).</p>
      <p><strong>Per quanti anni vanno detenuti gli investimenti per l'esenzione?</strong> Almeno 5 anni.</p>
      <p><strong>Limiti del PIR alternativo?</strong> 300.000€ l'anno, fino a 1.500.000€.</p>
      <p><strong>Cosa comporta la decadenza dal regime?</strong> Pagamento delle imposte non versate più interessi, senza sanzioni punitive.</p>
    </>
  ),

  // ── EP-13 · I contratti: forma, invalidità, inadempimento (trascrizione) ──
  "contratti": (
    <>
      <p>
        I contratti sono l'architettura invisibile dell'economia: ogni transazione vi poggia. Qui
        vediamo le <strong>fondamenta</strong> (gli elementi essenziali), le <strong>patologie</strong>
        {" "}(nullità e annullabilità) e cosa accade quando un contratto, pur nato sano, si scioglie per
        inadempimento.
      </p>

      <h2>Gli elementi essenziali (art. 1325)</h2>
      <p>
        Senza questi un contratto per la legge <strong>non esiste</strong>: l'<strong>accordo</strong>
        {" "}delle parti, la <strong>causa</strong>, l'<strong>oggetto</strong> e — in certi casi — la{" "}
        <strong>forma</strong>.
      </p>
      <ul>
        <li>
          <strong>La causa</strong> non è la motivazione soggettiva, ma la <em>funzione
          economico-sociale</em> del contratto. Se la funzione è qualcosa che l'ordinamento disapprova
          (per esempio un accordo per fare insider trading), la causa è illecita e il contratto è{" "}
          <strong>nullo</strong>, come se non fosse mai esistito.
        </li>
        <li>
          <strong>La forma</strong>: è un mito che un contratto debba essere sempre scritto — la
          maggior parte sono verbali e validissimi. Ma quando la legge richiede una forma{" "}
          <em>ad substantiam</em> (per esempio la compravendita di immobili o le locazioni
          ultranovennali), senza la forma scritta il contratto è <strong>nullo</strong>.
        </li>
      </ul>

      <h2>Le patologie: nullità vs annullabilità</h2>
      <ul>
        <li>
          <strong>Nullità</strong> — il difetto strutturale "insanabile": manca un elemento
          essenziale, oppure causa/oggetto sono illeciti o impossibili. Il contratto è inefficace da
          subito; la nullità è <strong>insanabile</strong>, l'azione è <strong>imprescrittibile</strong>
          {" "}e rilevabile d'ufficio.
        </li>
        <li>
          <strong>Annullabilità</strong> — il vizio "meno grave" e sanabile: <strong>incapacità</strong>
          {" "}di una parte o <strong>vizi del consenso</strong> (errore, violenza, dolo). Il contratto
          produce effetti finché non viene annullato su domanda della parte protetta (azione di norma
          entro <strong>cinque anni</strong>).
        </li>
      </ul>

      <h2>Quando il contratto si scioglie: l'inadempimento</h2>
      <p>
        Un contratto valido può sciogliersi per <strong>risoluzione</strong>: per{" "}
        <strong>inadempimento</strong> (di non scarsa importanza), per <strong>impossibilità
        sopravvenuta</strong> o per <strong>eccessiva onerosità sopravvenuta</strong>. La parte non
        inadempiente può chiedere l'adempimento o la risoluzione, oltre al risarcimento del danno.
      </p>

      <h2>Domande d'esame</h2>
      <p><strong>Quali sono gli elementi essenziali del contratto?</strong> Accordo, causa, oggetto e (quando richiesta) forma — art. 1325.</p>
      <p><strong>Un accordo con causa illecita: nullo o annullabile?</strong> Nullo.</p>
      <p><strong>Un vizio del consenso (errore, violenza, dolo): quale patologia?</strong> Annullabilità.</p>
      <p><strong>La compravendita di un immobile può essere verbale?</strong> No: serve la forma scritta <em>ad substantiam</em>.</p>
    </>
  ),

  // ── EP-10 · IVASS, vigilanza assicurativa e previdenza (trascrizione video) ─
  "ivass-previdenza-tfr-fondi-pensione": (
    <>
      <p>
        Il settore assicurativo e previdenziale è uno dei blocchi più densi del programma OCF:
        autorizzazioni, vigilanza, intermediari e regole di comportamento. Qui la mappa per orientarsi.
      </p>

      <h2>Riserva di attività e separazione dei rami</h2>
      <p>
        Solo le <strong>imprese autorizzate</strong> possono operare, e vige la <strong>separazione</strong> tra{" "}
        <em>rami vita</em> e <em>rami danni</em> (oggetto sociale distinto). Unica eccezione:
        un'impresa vita può gestire anche i rami danni <strong>infortuni e malattia</strong>. Le forme
        societarie ammesse: S.p.A., cooperativa, mutua assicuratrice, società europea. Le imprese devono
        costituire <strong>riserve tecniche</strong> (best estimate + margine di rischio) a copertura
        degli impegni futuri.
      </p>
      <p>
        Attenzione ai <strong>rischi non assicurabili</strong>: è vietato assicurare il pagamento di
        <strong> sanzioni amministrative</strong> e il prezzo del riscatto in caso di sequestro di persona.
      </p>

      <h2>IVASS e la vigilanza</h2>
      <p>
        L'<strong>IVASS</strong> (Istituto per la Vigilanza sulle Assicurazioni) autorizza e vigila su
        imprese, gruppi e <strong>intermediari</strong>, sia vita sia danni, diretta e riassicurazione.
        La vigilanza è spesso <strong>condivisa</strong>: su una banca che colloca polizze intervengono
        Banca d'Italia, CONSOB, IVASS e COVIP, ognuna per competenza.
      </p>

      <h2>Gli intermediari e il RUI</h2>
      <p>Il <strong>Registro Unico degli Intermediari</strong> ha sezioni distinte:</p>
      <ul>
        <li><strong>Sez. A — agenti</strong> e <strong>Sez. B — broker</strong>: richiedono il superamento di una <strong>prova di idoneità</strong> organizzata dall'IVASS; i broker aderiscono obbligatoriamente a un <strong>fondo di garanzia</strong>.</li>
        <li><strong>Sez. D</strong> — banche, SIM, Poste (addetti interni); <strong>Sez. E</strong> — collaboratori degli iscritti in A/B.</li>
        <li>Il consulente finanziario abilitato all'offerta fuori sede che <em>collabora</em> con un intermediario si iscrive in Sez. E.</li>
      </ul>
      <p>
        Regola chiave a tutela del cliente: l'impresa risponde <strong>in solido</strong> per i danni
        causati dagli intermediari a cui ha affidato l'incarico.
      </p>

      <h2>Regole di comportamento (i tranelli d'esame)</h2>
      <ul>
        <li>Prima del contratto va valutata l'<strong>adeguatezza</strong> alle esigenze del cliente.</li>
        <li><strong>Divieto di incassare contante</strong> per i premi delle polizze vita.</li>
        <li>L'obbligo di <strong>best execution</strong> non si applica agli agenti assicurativi.</li>
        <li>I test MiFID II di adeguatezza/appropriatezza <strong>non</strong> si applicano agli agenti quando distribuiscono polizze dei <strong>rami danni</strong>.</li>
      </ul>

      <h2>Previdenza complementare</h2>
      <p>
        Vigila la <strong>COVIP</strong>. Il <strong>TFR</strong> può essere destinato alla previdenza
        complementare tramite i fondi pensione: <strong>negoziali</strong> (chiusi, di categoria),{" "}
        <strong>aperti</strong> e i <strong>PIP</strong> (piani individuali pensionistici). L'adesione è
        volontaria e gode di agevolazioni fiscali sui contributi.
      </p>

      <h2>Domande d'esame</h2>
      <p><strong>Un'impresa vita può fare anche rami danni?</strong> Solo infortuni e malattia.</p>
      <p><strong>Si può assicurare il pagamento di una multa?</strong> No (sanzioni amministrative non assicurabili).</p>
      <p><strong>Agenti e broker: come si accede al RUI?</strong> Con una prova di idoneità IVASS (sez. A e B).</p>
      <p><strong>Chi risponde dei danni causati dall'intermediario?</strong> L'impresa, in solido.</p>
    </>
  ),

  // ── EP-11 · Assicurazioni ramo danni e vita (trascrizione video) ───────────
  "assicurazioni-danni-vita": (
    <>
      <p>
        Due mondi con logiche opposte: il <strong>ramo danni</strong> (funzione indennitaria) e il
        <strong> ramo vita</strong> (funzione previdenziale/di risparmio). Ripasso per l'Esame OCF.
      </p>

      <h2>Ramo danni: la funzione indennitaria</h2>
      <p>
        Lo scopo è <strong>indennitario</strong>: rimborsare il danno economico subito, né più né meno
        (principio che vieta l'arricchimento). Coprono i <strong>danni diretti</strong> e le conseguenze
        immediate; i danni <strong>indiretti</strong> (es. mancato guadagno) sono di norma esclusi salvo
        patto contrario.
      </p>

      <h2>Il sinistro: obblighi e tempi</h2>
      <ul>
        <li><strong>Avviso di sinistro entro 3 giorni</strong> da quando è avvenuto o se ne ha avuto notizia.</li>
        <li>Se l'omissione dell'avviso o del salvataggio è <strong>dolosa</strong>, si perde del tutto il diritto all'indennizzo.</li>
        <li>Le <strong>spese di salvataggio</strong> sono a carico dell'assicuratore (in proporzione al valore), salvo siano state sostenute "<em>inconsideratamente</em>".</li>
        <li>I <strong>vizi intrinseci</strong> non dichiarati della cosa possono escludere o ridurre l'indennizzo.</li>
      </ul>

      <h2>Valore assicurato: sovra e sotto-assicurazione</h2>
      <p>
        Se la somma assicurata supera il valore reale (<strong>soprassicurazione</strong>) l'indennizzo
        resta limitato al danno effettivo; se è inferiore (<strong>sottoassicurazione</strong>) opera la
        <strong> regola proporzionale</strong>: l'assicuratore paga in proporzione al rapporto tra somma
        assicurata e valore.
      </p>

      <h2>Ramo vita</h2>
      <p>
        Polizze <strong>caso morte</strong>, <strong>caso vita</strong> e <strong>miste</strong>. Il
        contraente designa il <strong>beneficiario</strong> (anche con testamento) e può revocarlo. Punto
        d'esame: le somme dovute al beneficiario <strong>non entrano nell'asse ereditario</strong> e sono
        <strong> impignorabili e insequestrabili</strong> (art. 1923 c.c.). Possibili il <strong>riscatto</strong>{" "}
        e la <strong>riduzione</strong> della polizza.
      </p>

      <h2>Domande d'esame</h2>
      <p><strong>Entro quando va dato l'avviso di sinistro?</strong> 3 giorni.</p>
      <p><strong>Cosa comporta l'omissione dolosa dell'avviso?</strong> Perdita totale dell'indennizzo.</p>
      <p><strong>Somma assicurata inferiore al valore: cosa si applica?</strong> La regola proporzionale (sottoassicurazione).</p>
      <p><strong>Le somme della polizza vita al beneficiario sono pignorabili?</strong> No (art. 1923).</p>
    </>
  ),

  // ── EP-12 · Il contratto di assicurazione: principi (differenziato da ep-11) ─
  "contratto-di-assicurazione": (
    <>
      <p>
        Se l'episodio sulle <a href="/it/ocf/assicurazioni-previdenza/assicurazioni-danni-vita">assicurazioni
        danni e vita</a> guarda ai <em>prodotti</em>, qui guardiamo al <strong>contratto</strong> in sé: la
        struttura giuridica comune a ogni polizza (art. 1882 e seguenti c.c.).
      </p>

      <h2>Definizione ed elementi essenziali</h2>
      <p>
        L'assicurazione è il contratto con cui l'assicuratore, contro il pagamento di un <strong>premio</strong>,
        si obbliga a rivalere l'assicurato del danno (rami danni) o a pagare un capitale/rendita al
        verificarsi di un evento della vita umana (ramo vita) — art. 1882. Elementi centrali: il
        <strong> rischio</strong>, il <strong>premio</strong> e il <strong>sinistro</strong>. Senza rischio
        il contratto è <strong>nullo</strong> (art. 1895).
      </p>

      <h2>La buona fede: dichiarazioni inesatte e reticenze</h2>
      <p>Il cuore del contratto è la corretta rappresentazione del rischio da parte del contraente:</p>
      <ul>
        <li><strong>Con dolo o colpa grave</strong> (art. 1892): l'assicuratore può chiedere l'<strong>annullamento</strong> del contratto.</li>
        <li><strong>Senza dolo né colpa grave</strong> (art. 1893): l'assicuratore può <strong>recedere</strong>; se il sinistro avviene prima, l'indennità si riduce in proporzione.</li>
      </ul>

      <h2>Premio, durata e prescrizione</h2>
      <p>
        Il <strong>premio</strong> è dovuto in via anticipata; il mancato pagamento può <strong>sospendere</strong>{" "}
        la copertura. Il contratto ha una durata e regole di disdetta/recesso. I diritti derivanti dal
        contratto si <strong>prescrivono in due anni</strong> (dieci anni per le somme del ramo vita dovute
        al beneficiario, secondo la disciplina vigente).
      </p>

      <h2>La surrogazione dell'assicuratore</h2>
      <p>
        Pagato l'indennizzo, l'assicuratore <strong>subentra</strong> nei diritti dell'assicurato verso il
        terzo responsabile del danno (art. 1916): è il meccanismo che evita che il danneggiato incassi due
        volte e che il responsabile resti impunito.
      </p>

      <h2>Domande d'esame</h2>
      <p><strong>Contratto senza rischio: che sorte?</strong> Nullo (art. 1895).</p>
      <p><strong>Dichiarazione inesatta con dolo: rimedio dell'assicuratore?</strong> Annullamento (art. 1892).</p>
      <p><strong>Cos'è la surrogazione?</strong> L'assicuratore subentra nei diritti verso il terzo responsabile (art. 1916).</p>
    </>
  ),

  // ── EP-16 · Titoli di credito e garanzie (trascrizione video) ──────────────
  "titoli-di-credito-garanzie": (
    <>
      <p>
        Dietro un assegno girato o un'azione ceduta c'è una "macchina legale" costruita sulla
        <strong> certezza formale</strong>: la forma è la sostanza. Ripasso per l'Esame OCF.
      </p>

      <h2>I tre principi dei titoli di credito</h2>
      <ul>
        <li><strong>Letteralità</strong>: vale ciò che è scritto sul titolo, nulla di più.</li>
        <li><strong>Autonomia</strong>: chi acquista il titolo acquista un diritto proprio, indipendente dai rapporti dei precedenti portatori.</li>
        <li><strong>Astrattezza</strong>: il titolo circola a prescindere dalla causa che lo ha originato.</li>
      </ul>

      <h2>La circolazione: girata e consegna</h2>
      <p>
        La <strong>girata parziale è nulla</strong>: il diritto deve restare intero e inequivocabile. La
        girata va scritta <strong>sul titolo stesso</strong>, creando una <strong>catena</strong> visibile e
        ininterrotta di portatori. Per i titoli <strong>al portatore</strong> vale una regola che spiazza:
        la proprietà si trasferisce con la <strong>consegna</strong> del documento, non con il semplice
        accordo — massimo rischio in cambio di massima velocità di circolazione.
      </p>

      <h2>Tipologie</h2>
      <p>
        Per legge di circolazione: <strong>al portatore</strong> (consegna), <strong>all'ordine</strong>{" "}
        (girata, es. cambiale e assegno), <strong>nominativi</strong> (doppia annotazione). Titoli tipici:
        la <strong>cambiale</strong> (pagherò e tratta) e l'<strong>assegno</strong> (bancario e circolare).
      </p>

      <h2>Le garanzie</h2>
      <ul>
        <li><strong>Reali</strong> (gravano su un bene): <strong>pegno</strong> (beni mobili) e <strong>ipoteca</strong> (beni immobili/mobili registrati).</li>
        <li><strong>Personali</strong> (un soggetto risponde col proprio patrimonio): <strong>fideiussione</strong> e, sui titoli di credito, l'<strong>avallo</strong> (garanzia tipica della cambiale).</li>
      </ul>

      <h2>Domande d'esame</h2>
      <p><strong>La girata parziale è valida?</strong> No, è nulla.</p>
      <p><strong>Come si trasferisce un titolo al portatore?</strong> Con la consegna del documento.</p>
      <p><strong>Differenza pegno/ipoteca?</strong> Pegno su beni mobili, ipoteca su immobili/mobili registrati.</p>
      <p><strong>Cos'è l'avallo?</strong> La garanzia personale tipica della cambiale.</p>
    </>
  ),
};
